import React, { useLayoutEffect, useRef } from "react";
import styled from "@emotion/styled";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ContainerComponents from "./ContainerComponents/ContainerComponents";

gsap.registerPlugin(ScrollTrigger);

/* =====================================================
   STYLED CONTAINER
===================================================== */

const ContainerDiv = styled.div`
  width: 100%;
  overflow: hidden;

  .section {
    width: 100%;
    height: 100dvh;
    display: flex;
    align-items: flex-end;
  }

  .pin-section {
    position: relative;
    overflow-x: clip;
    border-top: 2px dashed var(--color-surface50);
    border-bottom: 2px dashed var(--color-surface50);
  }

  .content {
  z-index: 1;
  }


  .academy-item {
    display: none;
    align-items: center;
    background: #1d1e20;
    margin-bottom: 1rem;
    border-radius: 4rem;
    padding: 1.5rem 3.5rem 1.5rem 2rem;
    width: fit-content;
    max-width: 60rem;
  }

  .item-li {
    text-align: center;
  }

  .icon {
    font-size: 2rem;
    margin-right: 1rem;
  }

  .title {
    font-size: 2rem;
    font-weight: 700;
    color: #fffce1;
  }

  .paragraph {
    height: 0;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
  }

  .academy-item.in-view {
    display: flex;

    .icon {
      display: none;
    }

    .title {
      color: var(--brand-1);
      margin-bottom: 0.5rem;
      transition: color 1s ease;
    }

    .paragraph {
      height: 100%;
      max-height: 30rem;
      opacity: 1;
      transition: max-height 2s ease, opacity 1s ease;
    }
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    width: fit-content;
  }

  .fill {
    position: absolute;
    top: 0;
    left: 0;
    width: 2px;
    height: 100%;
    background: var(--color-shockingly-green);
    transform-origin: top;
  }

  .slide {
    position: absolute;
    inset: 0;
    opacity: 0;
    visibility: hidden;
  }

  .slide img,
  .slide video {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  .slider-img img {
    object-fit: contain;
    position: absolute;
    bottom: 0;
    top: -5rem;
    right: 0;
    max-width: 75rem;
    padding: 2rem;
  }

  .cover {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(
      to top,
      rgb(0, 0, 0) 15%,
      transparent 50%,
      transparent 100%
    );
  }

  @media only screen and (min-width: 992px) {
    .section {
      align-items: center;
    }

    .academy-item {
      display: flex;
    }

    .item-li {
      text-align: left;
    }

    .slider-img img {
      top: 15rem;
      right: 5rem;
      max-width: 75rem;
    }

    .slide video {
      transform: translateX(26rem);
    }

    .cover {
    background: linear-gradient(
      to right,
      rgb(0, 0, 0) 15%,
      transparent 50%,
      transparent 100%
    );
  }
`;

/* =====================================================
   COMPONENT
===================================================== */

const ContainerAcademy = () => {
  const rootRef = useRef(null);

  const array_values = [
    {
      _id: "1",
      title: {
        heading: "Led by Artists, Shaped by Experience",
        paragraph:
          "LT Mastery is guided by Luis & Tay, artists shaped by years of teaching and performing worldwide. Their approach blends technical precision, musical sensitivity, and real partner connection. So you learn from instructors who understand how dancers truly grow.",
      },
      content: {
        type: "video",
        url_cotent:
          "https://res.cloudinary.com/djiqf3usa/video/upload/v1769553693/Clients/LTMastery/Frontpage/Videos/start-sensual-bachata-from-zero-no-partner-required_u4z5b1.mov",
      },
    },
    {
      _id: "2",
      title: {
        heading: "A Complete Learning Ecosystem",
        paragraph:
          "LT Mastery goes beyond courses. It’s a connected training system where lessons, technique, challenges, styling, and practice work together, keeping your learning organized, intentional, and focused.",
      },
      content: {
        type: "image",
        url_cotent:
          "https://res.cloudinary.com/djiqf3usa/image/upload/v1769455688/Clients/LTMastery/Frontpage/Images/Imagenes%20con%20SEO/start-bachata-from-zero-no-partner-required_sxrees.webp",
      },
    },
    {
      _id: "3",
      title: {
        heading: "Training That Adapts to You",
        paragraph:
          "Your training evolves as you progress. LT Mastery tracks what you’ve done and guides what’s next, so you always know where to focus, without guessing or starting over.",
      },
      content: {
        type: "image",
        url_cotent:
          "https://res.cloudinary.com/djiqf3usa/image/upload/v1769454674/Clients/LTMastery/Frontpage/Images/Imagenes%20con%20SEO/structured-sensual-bachata-training-system-dashboard_d0trnm.webp",
      },
    },
    {
      _id: "4",
      title: {
        heading: "From Individual Practice to Shared Growth",
        paragraph:
          "Train solo when you want, then grow together. Dancers from 20+ countries train inside LT Mastery through challenges, feedback, rewards, and a focused global community",
      },
      content: {
        type: "video",
        url_cotent:
          "https://res.cloudinary.com/djiqf3usa/video/upload/v1770419826/Clients/LTMastery/Frontpage/Videos/structured-sensual-bachata-training-system-dashboard_sozvwt.mp4",
      },
    },
  ];

  useLayoutEffect(() => {
    if (!rootRef.current) return;

    const ctx = gsap.context(() => {
      const root = rootRef.current;

      const items = Array.from(root.querySelectorAll(".academy-item"));
      const slides = Array.from(root.querySelectorAll(".academy-slide"));
      const fill = root.querySelector(".fill");
      const pinSection = root.querySelector(".pin-section");

      if (
        !items.length ||
        items.length !== slides.length ||
        !fill ||
        !pinSection
      ) {
        return;
      }

      const total = items.length;
      let lastIndex = -1;

      items.forEach((item) => item.classList.remove("in-view"));
      items[0].classList.add("in-view");

      slides.forEach((slide) => gsap.set(slide, { autoAlpha: 0 }));
      gsap.set(slides[0], { autoAlpha: 1 });

      gsap.set(fill, { scaleY: 1 / total });

      const syncVideos = (activeIndex) => {
        slides.forEach((slide, i) => {
          const video = slide.querySelector("video");
          if (!video) return;

          if (i === activeIndex) {
            try {
              video.loop = true;
              video.currentTime = 0;
              video.play();
            } catch (_) {}
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      };

      // 🔥 autoplay inicial si el primero es video
      syncVideos(0);
      lastIndex = 0;

      gsap
        .timeline({
          scrollTrigger: {
            trigger: pinSection,
            start: "top top",
            end: `+=${total * 50}%`,
            pin: true,
            scrub: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,

            onUpdate: (self) => {
              const index = Math.min(
                total - 1,
                Math.floor(self.progress * total)
              );

              if (index === lastIndex) return;
              lastIndex = index;

              items.forEach((item, i) => {
                item.classList.toggle("in-view", i === index);
              });

              slides.forEach((slide, i) => {
                gsap.set(slide, { autoAlpha: i === index ? 1 : 0 });
              });

              syncVideos(index);
            },
          },
        })
        .to(fill, { scaleY: 1, ease: "none" }, 0);
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <ContainerDiv ref={rootRef}>
      <div className="section pin-section">
        <div className="cover"></div>
        <div className="t-container content">
          <ul className="list t-pr">
            {array_values.map(({ _id, title }) => (
              <li key={_id} className="academy-item">
                <i className="icon fa-solid fa-caret-right"></i>
                <span className="item-li">
                  <span className="title">{title.heading}</span>
                  <p className="paragraph">{title.paragraph}</p>
                </span>
              </li>
            ))}
          </ul>

          <div className="fill" />
        </div>

        <div className="container-sliders">
          {array_values.map((info) => (
            <ContainerComponents
              key={info._id}
              className="academy-slide"
              options={{ objinfo: info }}
            />
          ))}
        </div>
      </div>
    </ContainerDiv>
  );
};

export default ContainerAcademy;
